import execa from 'execa';
import kebabCase from 'lodash.kebabcase';
import { styleText } from 'node:util';
import path from 'path';
import yargs from 'yargs';
import { hideBin } from 'yargs/helpers';
import { getStartupVersion } from '../core';
import { CommandName, log } from '../utils';
import { commandRegistry } from './commands/registry';
import type { CommandOptions } from './commands/types';
import { checkStartupLocation, getExitCode, setNodeOptions } from './utils';

const yargsInstance = yargs(hideBin(process.argv));

Object.values(CommandName)
    .reduce((y, name) => addCommand(name, y), yargsInstance)
    .usage('Usage: startup <command> [options]')
    .epilogue(
        styleText(['bold', 'cyan'], `startup cli v${getStartupVersion()}\n`) +
            `Run ${styleText(['bold', 'cyan'], 'startup <command> --help')} to see command options.\n` +
            `For detailed documentation, see ${styleText(['bold', 'cyan'], 'https://docs.st.dev/docs/frontend/uikit/startup')}.\n`
    )
    .demandCommand(1, '')
    .strictCommands()
    .updateStrings({
        'Unknown command: %s': {
            one: styleText(['bold', 'red'], 'Unknown command: %s'),
            other: styleText(['bold', 'red'], 'Unknown command: %s'),
        } as any,
    })
    .version()
    .wrap(Math.min(100, yargsInstance.terminalWidth()))
    .help()
    .parse();

function addCommand(name: CommandName, y: typeof yargs) {
    const entry = commandRegistry[name];

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { description, options: { _, ...options } = {} } = entry;
    const nameWithArgs = name + (_?.description ? ` ${_.description}` : '');
    const groupedOptions = Object.entries(options).reduce((result, [key, value]) => {
        const suffix = /\W+$/.exec(key)?.[0] ?? ''; // fix for kebabCase dropping `\u00A0` suffixes
        result[kebabCase(key) + suffix] = { group: 'Command Options:', ...value };
        return result;
    }, {} as CommandOptions);
    const handler = (argv: any) => handleCommand(name, argv);
    if (description) {
        y.command(nameWithArgs, description, groupedOptions, handler);
    } else {
        y.command(nameWithArgs, false, groupedOptions, handler);
    }
    return y;
}

async function handleCommand(name: CommandName, argv: any) {
    const entry = commandRegistry[name];

    checkNodeVersion();
    checkStartupLocation(name, entry);

    if (setNodeOptions(name, entry)) {
        log.debug('run', `Running ${name} in child process with amended NODE_OPTIONS`);
        execa(process.argv[0], process.argv.slice(1), { stdio: 'inherit' }).catch(reason => {
            process.exit(getExitCode(reason));
        });
        return;
    }

    const CommandClass = await entry.load();
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const command = new CommandClass({ ...argv, _: argv._.slice(1) });

    command.watchForParentExit(name);

    command.execute().catch(() => {
        process.exit(1);
    });
}

function checkNodeVersion() {
    const nodeVersion = Number(process.versions.node.split('.')[0]);
    if (nodeVersion % 2 === 0 || process.env.SKIP_NODE_VERSION_CHECK) {
        return;
    }

    const { engines } = require(path.join(__dirname, '../../package.json'));
    log.error(
        `error: node v${nodeVersion} detected, only even-numbered LTS versions ${engines.node} are supported`
    );
    log.text('See https://nodejs.org/en/download for LTS versions');
    process.exit(1);
}
